import React from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { ChevronLeft } from "lucide-react";
import {
  AssessmentHistoryDetail,
  AssessmentHistoryQuestionReview,
} from "../../axios/quiz";
import useUserStore from "../../stores/userStore";
import {
  buildFillInBlankUserAnswerSegments,
  type QuestionPreviewSegment,
} from "../../utils/assessmentInteractive";
import {
  formatAssessmentPoints,
  formatAssessmentScore,
} from "../../utils/assessmentScore";
import {
  answerListStyles,
  answerOptionStyles,
  chosenAnswerLabelStyles,
  backButtonStyles,
  competencyChipStyles,
  competencyListStyles,
  competencyNameStyles,
  competencyRowStyles,
  competencyStatsStyles,
  detailHeaderRowStyles,
  detailSummaryStyles,
  itemMetaStyles,
  itemTitleStyles,
  justificationBoxStyles,
  openAnswerBoxStyles,
  questionCardStyles,
  questionIndexStyles,
  questionLabelRowStyles,
  questionTextStyles,
  questionsStackStyles,
  scoreBadgeStyles,
  sectionTitleStyles,
} from "./styles";

interface AssessmentHistoryDetailViewProps {
  detail: AssessmentHistoryDetail | null;
  loading: boolean;
  error: string | null;
  onBack: () => void;
  formatFinishedAt: (value: string) => string;
  formatScore: (earned: number, max: number, bonusScore?: number) => string;
}

type HistoryAnswerOptionVariant =
  | "chosen-correct"
  | "chosen-wrong"
  | "correct-other"
  | "neutral";

const INTERACTIVE_HISTORY_TYPES = new Set([
  "fill_in_blank",
  "ordering",
  "matching",
  "ordered_response",
]);

const isInteractiveHistoryQuestion = (
  question: AssessmentHistoryQuestionReview,
): boolean =>
  Boolean(
    question.question_type &&
      INTERACTIVE_HISTORY_TYPES.has(question.question_type),
  );

const getChosenAnswerVariant = (
  question: AssessmentHistoryQuestionReview,
): Extract<HistoryAnswerOptionVariant, "chosen-correct" | "chosen-wrong"> =>
  question.is_correct ? "chosen-correct" : "chosen-wrong";

const formatHistoryAnswerPoints = (
  score: number,
  bonusScore: number = 0,
): string =>
  score !== 0 ? ` (${formatAssessmentPoints(score, bonusScore)})` : "";

const renderHistoryChosenAnswerBlock = (
  question: AssessmentHistoryQuestionReview,
  content: React.ReactNode,
) => {
  const variant = getChosenAnswerVariant(question);
  const pointsSuffix = formatHistoryAnswerPoints(
    question.score,
    question.bonus_score,
  );

  return (
    <Box sx={answerListStyles}>
      <Box sx={answerOptionStyles(variant)}>
        <Typography sx={chosenAnswerLabelStyles(variant)}>Your answer</Typography>
        <Box component="span" sx={{ display: "block" }}>
          {content}
          {pointsSuffix}
          {!content && !pointsSuffix ? "0 pts" : null}
        </Box>
      </Box>
    </Box>
  );
};

const normalizeAnswerText = (value: string): string => value.trim();

const getAnswerOptionVariant = (
  question: AssessmentHistoryQuestionReview,
  answerText: string,
  answerPoints: number,
): HistoryAnswerOptionVariant => {
  const isChosen =
    normalizeAnswerText(answerText) ===
    normalizeAnswerText(question.chosen_answer);

  if (isChosen) {
    return answerPoints > 0 ? "chosen-correct" : "chosen-wrong";
  }

  const isOtherScoredOption =
    answerPoints > 0 ||
    (question.correct_answer &&
      normalizeAnswerText(answerText) ===
        normalizeAnswerText(question.correct_answer));

  if (isOtherScoredOption) {
    return "correct-other";
  }

  return "neutral";
};

const formatCompetencyStats = (
  correct: number,
  total: number,
  scoreEarned?: number,
  scoreMax?: number,
  bonusScore?: number,
): string => {
  const fraction = `${correct}/${total} correct`;
  if (
    typeof scoreEarned === "number" &&
    typeof scoreMax === "number" &&
    scoreMax > 0
  ) {
    return `${fraction} · ${formatAssessmentScore(
      scoreEarned,
      scoreMax,
      bonusScore,
    )}`;
  }
  return fraction;
};

const renderPreviewSegments = (segments: QuestionPreviewSegment[]) => (
  <>
    {segments.map((segment, index) =>
      segment.bold ? (
        <Box
          component="span"
          key={`history-segment-${index}`}
          sx={{ fontWeight: 800 }}
        >
          {segment.text}
        </Box>
      ) : (
        <span key={`history-segment-${index}`}>{segment.text}</span>
      ),
    )}
  </>
);

export const AssessmentHistoryDetailView: React.FC<
  AssessmentHistoryDetailViewProps
> = ({
  detail,
  loading,
  error,
  onBack,
  formatFinishedAt,
  formatScore,
}) => {
  const getFocusDisplayName = useUserStore((state) => state.getFocusDisplayName);
  const needsReviewItems =
    detail?.competency_summary.filter((item) => item.needs_review) ?? [];
  const strongItems =
    detail?.competency_summary.filter((item) => !item.needs_review) ?? [];

  return (
    <>
      <Box sx={detailHeaderRowStyles}>
        <Box
          component="button"
          type="button"
          onClick={onBack}
          sx={backButtonStyles}
          aria-label="Back to contests list"
        >
          <ChevronLeft size={18} />
          Back
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Typography sx={itemMetaStyles}>{error}</Typography>
      )}

      {!loading && !error && detail && (
        <>
          <Box sx={detailSummaryStyles}>
            <Typography sx={itemTitleStyles}>
              {detail.title || "Contest"}
            </Typography>
            {detail.focus ? (
              <Typography sx={itemMetaStyles}>
                {getFocusDisplayName(detail.focus)}
              </Typography>
            ) : null}
            <Typography sx={itemMetaStyles}>
              {formatFinishedAt(detail.finished_at)}
            </Typography>
            {typeof detail.user_score === "number" ||
            typeof detail.mandatory_score === "number" ? (
              <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.35 }}>
                <Typography sx={itemMetaStyles}>
                  Mandatory: {detail.mandatory_score ?? 0} /{" "}
                  {detail.mandatory_max_score ?? 0}
                </Typography>
                <Typography sx={itemMetaStyles}>
                  Bonus questions: {detail.bonus_question_score ?? 0} /{" "}
                  {detail.bonus_question_max_score ?? 0}
                </Typography>
                <Typography sx={itemMetaStyles}>
                  Total user score: {detail.user_score ?? detail.score_earned}
                </Typography>
                <Typography sx={itemMetaStyles}>
                  Speed/streak bonus: +
                  {detail.gamification_bonus_score ?? detail.bonus_score ?? 0}
                </Typography>
              </Box>
            ) : (
              <Typography sx={itemMetaStyles}>
                {formatScore(
                  detail.score_earned,
                  detail.score_max,
                  detail.bonus_score,
                )}
              </Typography>
            )}
          </Box>

          {needsReviewItems.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography sx={sectionTitleStyles}>Areas to improve</Typography>
              <Box sx={competencyListStyles}>
                {needsReviewItems.map((item) => (
                  <Box
                    key={item.competency}
                    sx={competencyRowStyles(true)}
                  >
                    <Typography sx={competencyNameStyles(false)}>
                      {item.competency}
                    </Typography>
                    <Typography sx={competencyStatsStyles(false)}>
                      {formatCompetencyStats(
                        item.correct,
                        item.total,
                        item.score_earned,
                        item.score_max,
                        item.bonus_score,
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}

          {strongItems.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography sx={sectionTitleStyles}>Strong areas</Typography>
              <Box sx={competencyListStyles}>
                {strongItems.map((item) => (
                  <Box
                    key={item.competency}
                    sx={competencyRowStyles(false)}
                  >
                    <Typography sx={competencyNameStyles(true)}>
                      {item.competency}
                    </Typography>
                    <Typography sx={competencyStatsStyles(true)}>
                      {formatCompetencyStats(
                        item.correct,
                        item.total,
                        item.score_earned,
                        item.score_max,
                        item.bonus_score,
                      )}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}

          <Typography sx={sectionTitleStyles}>Questions review</Typography>
          <Box sx={questionsStackStyles}>
            {detail.questions.map((question) => {
              const isFillInBlank = question.question_type === "fill_in_blank";
              const isInteractive = isInteractiveHistoryQuestion(question);
              const isOpenText =
                question.question_type === "open_text" ||
                (!isInteractive &&
                  !question.answers?.length &&
                  question.chosen_answer.length > 0);

              return (
                <Box
                  key={`${question.question_index}-${question.question}`}
                  sx={questionCardStyles}
                >
                  <Box sx={questionLabelRowStyles}>
                    <Typography sx={questionIndexStyles}>
                      Question {question.question_index}
                    </Typography>
                    <Chip
                      size="small"
                      label={question.is_bonus ? "Bonus" : "Mandatory"}
                      color={question.is_bonus ? "warning" : "default"}
                    />
                    {question.competency ? (
                      <Typography component="span" sx={competencyChipStyles}>
                        {question.competency}
                      </Typography>
                    ) : null}
                    <Typography sx={scoreBadgeStyles(question.is_correct)}>
                      {formatAssessmentScore(
                        question.score,
                        question.max_score,
                        question.bonus_score,
                      )}
                    </Typography>
                  </Box>

                  <Typography component="div" sx={questionTextStyles}>
                    {isFillInBlank
                      ? renderPreviewSegments(
                          buildFillInBlankUserAnswerSegments(
                            question.question,
                            "",
                          ),
                        )
                      : question.question}
                  </Typography>

                  {isInteractive ? (
                    <>
                      {renderHistoryChosenAnswerBlock(
                        question,
                        isFillInBlank
                          ? question.chosen_answer.trim()
                            ? renderPreviewSegments(
                                buildFillInBlankUserAnswerSegments(
                                  question.question,
                                  question.chosen_answer,
                                ),
                              )
                            : "—"
                          : question.chosen_answer || "—",
                      )}
                      {question.justification ? (
                        <Box sx={justificationBoxStyles(question.is_correct)}>
                          {question.justification}
                        </Box>
                      ) : null}
                    </>
                  ) : isOpenText ? (
                    <>
                      <Typography sx={itemMetaStyles}>Your answer</Typography>
                      <Box sx={openAnswerBoxStyles}>
                        {question.chosen_answer || "—"}
                      </Box>
                      {question.justification ? (
                        <Box sx={justificationBoxStyles(question.is_correct)}>
                          {question.justification}
                        </Box>
                      ) : null}
                    </>
                  ) : question.answers?.length ? (
                    <Box sx={answerListStyles}>
                      {question.answers.map((answer) => {
                        const variant = getAnswerOptionVariant(
                          question,
                          answer.text,
                          answer.points,
                        );
                        const isChosen =
                          variant === "chosen-correct" ||
                          variant === "chosen-wrong";

                        return (
                          <Box
                            key={answer.text}
                            sx={answerOptionStyles(variant)}
                          >
                            {isChosen ? (
                              <Typography
                                sx={chosenAnswerLabelStyles(
                                  variant === "chosen-wrong"
                                    ? "chosen-wrong"
                                    : "chosen-correct",
                                )}
                              >
                                Your answer
                              </Typography>
                            ) : null}
                            <Box component="span" sx={{ display: "block" }}>
                              {answer.text}
                              {answer.points !== 0
                                ? ` (+${answer.points} pts)`
                                : ""}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <>
                      <Typography sx={itemMetaStyles}>
                        Your answer: {question.chosen_answer || "—"}
                      </Typography>
                      {question.correct_answer ? (
                        <Typography sx={itemMetaStyles}>
                          Correct answer: {question.correct_answer}
                        </Typography>
                      ) : null}
                      {question.justification ? (
                        <Box sx={justificationBoxStyles(question.is_correct)}>
                          {question.justification}
                        </Box>
                      ) : null}
                    </>
                  )}

                  {!isOpenText &&
                  !isInteractive &&
                  question.answers?.length &&
                  question.justification ? (
                    <Box sx={justificationBoxStyles(question.is_correct)}>
                      {question.justification}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>

          {detail.questions.length === 0 ? (
            <Typography sx={itemMetaStyles}>
              No question details available for this contest.
            </Typography>
          ) : null}
        </>
      )}
    </>
  );
};
